/**
 * Product Zoom Viewer
 * Interactive full-screen image zoom with pan & pinch gestures
 */

// Use pub-sub if available (access via window to avoid redeclaration errors)
if (!window.publish) window.publish = () => {};
if (!window.PUB_SUB_EVENTS) window.PUB_SUB_EVENTS = {};

if (!customElements.get('product-zoom-viewer')) {
  customElements.define(
    'product-zoom-viewer',
    class ProductZoomViewer extends HTMLElement {
      constructor() {
        super();

        // State
        this.state = {
          isOpen: false,
          currentIndex: 0,
          scale: 1,
          translateX: 0,
          translateY: 0,
          lastScale: 1,
          lastTranslateX: 0,
          lastTranslateY: 0,
        };

        // Touch tracking
        this.touch = {
          startX: 0,
          startY: 0,
          startDistance: 0,
          startScale: 1,
          lastTapTime: 0,
          isInteracting: false,
          isPinching: false,
          velocityX: 0,
          velocityY: 0,
        };

        // Mouse tracking (desktop)
        this.mouse = {
          isDragging: false,
          startX: 0,
          startY: 0,
        };

        // Images data
        this.images = [];
        this.originalTrigger = null;

        // Bind methods
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleDoubleClick = this.handleDoubleClick.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
      }

      connectedCallback() {
        this.render();
        this.cacheElements();
        this.attachEventListeners();
      }

      disconnectedCallback() {
        this.detachEventListeners();
      }

      render() {
        this.innerHTML = `
          <div class="product-zoom-viewer__content">
            <button class="product-zoom-viewer__close" aria-label="Close zoom viewer">
              <svg viewBox="0 0 24 24" fill="none">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            <div class="product-zoom-viewer__image-container">
              <div class="product-zoom-viewer__image-wrapper">
                <img class="product-zoom-viewer__image" src="" alt="" />
              </div>
            </div>

            <div class="product-zoom-viewer__loading"></div>

            <div class="product-zoom-viewer__indicators"></div>

            <div class="product-zoom-viewer__hint" style="display: none;">Pinch to zoom, drag to pan</div>
          </div>
        `;
      }

      cacheElements() {
        this.elements = {
          content: this.querySelector('.product-zoom-viewer__content'),
          closeButton: this.querySelector('.product-zoom-viewer__close'),
          imageContainer: this.querySelector('.product-zoom-viewer__image-container'),
          imageWrapper: this.querySelector('.product-zoom-viewer__image-wrapper'),
          image: this.querySelector('.product-zoom-viewer__image'),
          loading: this.querySelector('.product-zoom-viewer__loading'),
          indicators: this.querySelector('.product-zoom-viewer__indicators'),
          hint: this.querySelector('.product-zoom-viewer__hint'),
        };
      }

      attachEventListeners() {
        // Touch events
        this.elements.imageContainer.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        this.elements.imageContainer.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        this.elements.imageContainer.addEventListener('touchend', this.handleTouchEnd, { passive: false });

        // Mouse events (desktop)
        this.elements.imageContainer.addEventListener('mousedown', this.handleMouseDown);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
        this.elements.imageContainer.addEventListener('wheel', this.handleWheel, { passive: false });
        this.elements.imageContainer.addEventListener('dblclick', this.handleDoubleClick);

        // Close button
        this.elements.closeButton.addEventListener('click', this.handleClose);

        // Keyboard
        document.addEventListener('keydown', this.handleKeyDown);
      }

      detachEventListeners() {
        this.elements.imageContainer.removeEventListener('touchstart', this.handleTouchStart);
        this.elements.imageContainer.removeEventListener('touchmove', this.handleTouchMove);
        this.elements.imageContainer.removeEventListener('touchend', this.handleTouchEnd);
        this.elements.imageContainer.removeEventListener('mousedown', this.handleMouseDown);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        this.elements.imageContainer.removeEventListener('wheel', this.handleWheel);
        this.elements.imageContainer.removeEventListener('dblclick', this.handleDoubleClick);
        this.elements.closeButton.removeEventListener('click', this.handleClose);
        document.removeEventListener('keydown', this.handleKeyDown);
      }

      /**
       * Open the zoom viewer with images
       * @param {Array} images - Array of image objects with src, alt, etc.
       * @param {number} startIndex - Index of image to show first
       * @param {HTMLElement} trigger - Element that triggered the viewer
       */
      open(images, startIndex = 0, trigger = null) {
        this.images = images;
        this.state.currentIndex = startIndex;
        this.originalTrigger = trigger;

        // Reset state
        this.resetZoom(false);

        // Load current image
        this.loadImage(startIndex);

        // Update indicators
        this.updateIndicators();

        // Show viewer
        this.classList.add('is-visible');

        requestAnimationFrame(() => {
          this.classList.add('is-active');
        });

        // Prevent body scroll using Dawn's CSS class approach
        document.body.classList.add('overflow-hidden');

        // Set hint text for the current input type
        this.updateHintText();

        // Show hint briefly
        this.showHint();

        // Publish zoom open event
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS.mobileZoomOpen) {
          window.publish(window.PUB_SUB_EVENTS.mobileZoomOpen, {
            imageCount: this.images.length,
            startIndex: startIndex,
            trigger: trigger
          });
        }

        this.state.isOpen = true;
      }

      /**
       * Close the zoom viewer
       */
      close() {
        this.classList.remove('is-active');

        setTimeout(() => {
          this.classList.remove('is-visible');
          document.body.classList.remove('overflow-hidden');

          // Return focus to trigger element
          if (this.originalTrigger) {
            this.originalTrigger.focus();
          }

          // Publish zoom close event
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS.mobileZoomClose) {
            window.publish(window.PUB_SUB_EVENTS.mobileZoomClose, {
              finalIndex: this.state.currentIndex
            });
          }

          this.state.isOpen = false;
        }, 300);
      }

      handleClose() {
        this.close();
      }

      handleKeyDown(e) {
        if (!this.state.isOpen) return;

        switch (e.key) {
          case 'Escape':
            this.close();
            break;
          case 'ArrowLeft':
            if (this.state.scale === 1) {
              this.navigateToPrevious();
            }
            break;
          case 'ArrowRight':
            if (this.state.scale === 1) {
              this.navigateToNext();
            }
            break;
        }
      }

      /**
       * Load image at specific index
       */
      loadImage(index) {
        if (index < 0 || index >= this.images.length) {
          return;
        }

        const imageData = this.images[index];
        const img = this.elements.image;

        // Show loading
        this.elements.loading.classList.add('is-visible');
        img.classList.add('is-loading');

        // Load image
        const tempImg = new Image();
        tempImg.onload = () => {
          img.src = tempImg.src;
          img.alt = imageData.alt || '';
          this.elements.loading.classList.remove('is-visible');
          img.classList.remove('is-loading');
        };

        tempImg.onerror = () => {
          // Image failed to load - silently handle
        };

        // Use largest available image for zoom
        const imageSrc = imageData.src || imageData.dataset?.src || imageData.currentSrc;
        tempImg.src = imageSrc;
      }

      /**
       * Update gallery indicators
       */
      updateIndicators() {
        const indicators = this.elements.indicators;

        if (this.images.length <= 1) {
          indicators.classList.add('is-single');
          return;
        }

        indicators.classList.remove('is-single');
        indicators.innerHTML = this.images
          .map((_, i) => `
            <span class="product-zoom-viewer__indicator ${i === this.state.currentIndex ? 'is-active' : ''}"></span>
          `)
          .join('');
      }

      /**
       * Navigate to previous image
       */
      navigateToPrevious() {
        if (this.state.currentIndex > 0) {
          this.navigateToImage(this.state.currentIndex - 1);
        }
      }

      /**
       * Navigate to next image
       */
      navigateToNext() {
        if (this.state.currentIndex < this.images.length - 1) {
          this.navigateToImage(this.state.currentIndex + 1);
        }
      }

      /**
       * Navigate to specific image
       */
      navigateToImage(index) {
        if (index < 0 || index >= this.images.length) return;

        this.state.currentIndex = index;
        this.resetZoom(true);
        this.loadImage(index);
        this.updateIndicators();

        // Publish navigation event
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS.mobileZoomNavigate) {
          window.publish(window.PUB_SUB_EVENTS.mobileZoomNavigate, {
            newIndex: index,
            totalImages: this.images.length
          });
        }
      }

      /**
       * Touch start handler
       */
      handleTouchStart(e) {
        e.preventDefault();

        const touches = e.touches;
        this.touch.isInteracting = true;
        this.elements.imageWrapper.classList.add('is-interacting');

        if (touches.length === 1) {
          // Single touch - check for double tap or prepare for pan/swipe
          this.touch.startX = touches[0].clientX;
          this.touch.startY = touches[0].clientY;

          const now = Date.now();
          const timeSinceLastTap = now - this.touch.lastTapTime;

          // Double tap detection (within 300ms)
          if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
            this.handleDoubleTap(touches[0].clientX, touches[0].clientY);
            this.touch.lastTapTime = 0;
          } else {
            this.touch.lastTapTime = now;
          }

        } else if (touches.length === 2) {
          // Two touches - pinch zoom
          this.touch.isPinching = true;
          this.touch.startDistance = this.getDistance(touches[0], touches[1]);
          this.touch.startScale = this.state.scale;

          // Get center point between fingers
          const centerX = (touches[0].clientX + touches[1].clientX) / 2;
          const centerY = (touches[0].clientY + touches[1].clientY) / 2;
          this.touch.centerX = centerX;
          this.touch.centerY = centerY;
        }
      }

      /**
       * Touch move handler
       */
      handleTouchMove(e) {
        e.preventDefault();

        const touches = e.touches;

        if (touches.length === 2 && this.touch.isPinching) {
          // Pinch zoom
          const currentDistance = this.getDistance(touches[0], touches[1]);
          const scale = (currentDistance / this.touch.startDistance) * this.touch.startScale;

          // Constrain scale between 1x and 5x
          const clampedScale = Math.max(1, Math.min(5, scale));

          this.state.scale = clampedScale;
          this.updateTransform();

        } else if (touches.length === 1 && !this.touch.isPinching) {
          // Single touch - pan or swipe
          const deltaX = touches[0].clientX - this.touch.startX;
          const deltaY = touches[0].clientY - this.touch.startY;

          // Track velocity for momentum
          this.touch.velocityX = deltaX;
          this.touch.velocityY = deltaY;

          if (this.state.scale > 1) {
            // Panning while zoomed
            this.state.translateX = this.state.lastTranslateX + deltaX;
            this.state.translateY = this.state.lastTranslateY + deltaY;

            // Apply boundaries
            this.constrainPan();
            this.updateTransform();

          } else {
            // Swiping for gallery navigation (only horizontal)
            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);

            // Only swipe if mostly horizontal movement
            if (absX > absY && absX > 50) {
              // Visual feedback - slight translation
              this.state.translateX = deltaX * 0.3;
              this.updateTransform();
            }
          }
        }
      }

      /**
       * Touch end handler
       */
      handleTouchEnd(e) {
        e.preventDefault();

        this.touch.isInteracting = false;
        this.elements.imageWrapper.classList.remove('is-interacting');

        if (this.touch.isPinching) {
          // End of pinch
          this.touch.isPinching = false;

          // If zoomed out to 1x, reset position
          if (this.state.scale === 1) {
            this.resetZoom(true);
          } else {
            // Save current state
            this.state.lastScale = this.state.scale;
            this.state.lastTranslateX = this.state.translateX;
            this.state.lastTranslateY = this.state.translateY;
          }

        } else if (this.state.scale === 1 && Math.abs(this.touch.velocityX) > 50) {
          // Swipe gesture for navigation
          if (this.touch.velocityX > 50) {
            // Swipe right - previous image
            this.navigateToPrevious();
          } else if (this.touch.velocityX < -50) {
            // Swipe left - next image
            this.navigateToNext();
          }

          // Reset translation
          this.resetZoom(true);

        } else if (this.state.scale > 1) {
          // Save pan position
          this.state.lastTranslateX = this.state.translateX;
          this.state.lastTranslateY = this.state.translateY;
        }
      }

      handleMouseDown(e) {
        if (!this.state.isOpen) return;
        if (e.button !== 0) return;
        e.preventDefault();

        this.mouse.isDragging = true;
        this.mouse.startX = e.clientX;
        this.mouse.startY = e.clientY;
        this.state.lastTranslateX = this.state.translateX;
        this.state.lastTranslateY = this.state.translateY;
        this.elements.imageWrapper.classList.add('is-interacting');
      }

      handleMouseMove(e) {
        if (!this.state.isOpen || !this.mouse.isDragging) return;
        e.preventDefault();

        if (this.state.scale <= 1) return;

        const deltaX = e.clientX - this.mouse.startX;
        const deltaY = e.clientY - this.mouse.startY;

        this.state.translateX = this.state.lastTranslateX + deltaX;
        this.state.translateY = this.state.lastTranslateY + deltaY;

        this.constrainPan();
        this.updateTransform();
      }

      handleMouseUp() {
        if (!this.mouse.isDragging) return;

        this.mouse.isDragging = false;
        this.elements.imageWrapper.classList.remove('is-interacting');
        this.state.lastTranslateX = this.state.translateX;
        this.state.lastTranslateY = this.state.translateY;
      }

      handleWheel(e) {
        if (!this.state.isOpen) return;
        e.preventDefault();

        const previousScale = this.state.scale;
        const zoomFactor = Math.exp(-e.deltaY * 0.002);
        const newScale = Math.max(1, Math.min(5, previousScale * zoomFactor));

        if (newScale === previousScale) return;

        this.state.scale = newScale;

        if (newScale === 1) {
          this.state.translateX = 0;
          this.state.translateY = 0;
        }

        this.constrainPan();
        this.updateTransform();
        this.state.lastScale = this.state.scale;
        this.state.lastTranslateX = this.state.translateX;
        this.state.lastTranslateY = this.state.translateY;
      }

      handleDoubleClick(e) {
        if (!this.state.isOpen) return;
        e.preventDefault();
        this.handleDoubleTap(e.clientX, e.clientY);
      }

      /**
       * Handle double tap to zoom
       */
      handleDoubleTap(x, y) {
        if (this.state.scale > 1) {
          // Zoom out
          this.resetZoom(true);
        } else {
          // Zoom in to 2.5x centered on tap point
          const rect = this.elements.imageWrapper.getBoundingClientRect();
          const offsetX = x - rect.left - rect.width / 2;
          const offsetY = y - rect.top - rect.height / 2;

          this.state.scale = 2.5;
          this.state.translateX = -offsetX * 1.5;
          this.state.translateY = -offsetY * 1.5;

          this.constrainPan();
          this.updateTransform();

          this.state.lastScale = this.state.scale;
          this.state.lastTranslateX = this.state.translateX;
          this.state.lastTranslateY = this.state.translateY;
        }
      }

      /**
       * Reset zoom to default
       */
      resetZoom(animated = true) {
        this.state.scale = 1;
        this.state.translateX = 0;
        this.state.translateY = 0;
        this.state.lastScale = 1;
        this.state.lastTranslateX = 0;
        this.state.lastTranslateY = 0;

        if (!animated) {
          this.elements.imageWrapper.classList.add('is-interacting');
        }

        this.updateTransform();

        if (!animated) {
          requestAnimationFrame(() => {
            this.elements.imageWrapper.classList.remove('is-interacting');
          });
        }
      }

      /**
       * Update transform CSS
       */
      updateTransform() {
        const { scale, translateX, translateY } = this.state;
        this.elements.imageWrapper.style.transform =
          `translate(${translateX}px, ${translateY}px) scale(${scale})`;
      }

      /**
       * Constrain pan to image boundaries
       */
      constrainPan() {
        if (this.state.scale <= 1) {
          this.state.translateX = 0;
          this.state.translateY = 0;
          return;
        }

        const img = this.elements.image;
        const wrapper = this.elements.imageWrapper;

        // Get actual rendered dimensions
        const imgRect = img.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();

        // Calculate max pan based on zoomed image size
        const maxX = Math.max(0, (imgRect.width - wrapperRect.width / this.state.scale) / 2);
        const maxY = Math.max(0, (imgRect.height - wrapperRect.height / this.state.scale) / 2);

        // Constrain translation
        this.state.translateX = Math.max(-maxX, Math.min(maxX, this.state.translateX));
        this.state.translateY = Math.max(-maxY, Math.min(maxY, this.state.translateY));
      }

      /**
       * Get distance between two touch points
       */
      getDistance(touch1, touch2) {
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      /**
       * Show hint briefly
       */
      showHint() {
        this.elements.hint.style.display = ''; // Remove inline style to allow CSS to work
        this.elements.hint.classList.add('is-visible');
        setTimeout(() => {
          this.elements.hint.classList.remove('is-visible');
          this.elements.hint.style.display = 'none'; // Re-hide with inline style
        }, 2000);
      }

      updateHintText() {
        const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.elements.hint.textContent = isFinePointer
          ? 'Scroll to zoom, drag to pan'
          : 'Pinch to zoom, drag to pan';
      }
    }
  );
}

/**
 * Initialize zoom viewer on page load
 * Viewer is created lazily when product-zoom-override.js requests it
 */
// Expose function to create viewer on demand (called by product-zoom-override.js)
window.createProductZoomViewer = function() {
  if (!document.body) {
    setTimeout(window.createProductZoomViewer, 50);
    return null;
  }

  // Return existing viewer if already created
  const existing = document.querySelector('product-zoom-viewer');
  if (existing) {
    return existing;
  }

  // Create new viewer
  const viewer = document.createElement('product-zoom-viewer');
  document.body.appendChild(viewer);
  window.productZoomViewer = viewer;
  return viewer;
};
